import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldX, RefreshCw } from "lucide-react";

/**
 * Phase H2 follow-up — Schema-Drift Status-Card.
 *
 * Surfaces the daily 02:30 Vienna cron run from `schema_drift_runs`. Shows
 * one of three states:
 *   • OK    (green)  · 0 silent-sinks across all collections
 *   • DRIFT (amber)  · at least one collection has DB fields missing from
 *                     the Pydantic model (silent data-sink)
 *   • ERROR (rose)   · last cron run threw an exception
 *
 * Includes a "Jetzt prüfen" button for ad-hoc validation after a deploy.
 */
function _fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-AT", {
    dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Vienna",
  }); } catch { return iso; }
}

export default function SchemaDriftStatusCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/schema-drift/status");
      setData(data);
    } catch (e) {
      // 403 = non-system-admin; silently hide
      if (e.response?.status !== 403) {
        toast.error("Schema-Drift-Status konnte nicht geladen werden");
      }
      setData({ _forbidden: e.response?.status === 403 });
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function runNow() {
    setRunning(true);
    try {
      await api.post("/admin/schema-drift/run");
      toast.success("Schema-Drift-Prüfung abgeschlossen");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Prüfung fehlgeschlagen");
    } finally { setRunning(false); }
  }

  if (data?._forbidden) return null;

  const latest = data?.latest;
  const status = latest?.status || "—";
  const sinks = latest?.sinks_total ?? 0;

  let palette = "border-zinc-700 bg-zinc-900 text-zinc-300";
  let Icon = ShieldCheck;
  let badge = "Lade…";
  if (!loading && status === "ok") {
    palette = "border-emerald-500/30 bg-emerald-500/5 text-emerald-200";
    Icon = ShieldCheck; badge = "OK · 0 Silent-Sinks";
  } else if (!loading && status === "drift") {
    palette = "border-amber-500/30 bg-amber-500/5 text-amber-200";
    Icon = ShieldAlert; badge = `Drift · ${sinks} Sink(s)`;
  } else if (!loading && status === "error") {
    palette = "border-rose-500/30 bg-rose-500/5 text-rose-200";
    Icon = ShieldX; badge = "Fehler · Cron-Lauf abgebrochen";
  }

  return (
    <div className={`rounded p-5 border ${palette}`} data-testid="schema-drift-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" />
          <div>
            <h3 className="font-display font-semibold text-zinc-50">
              Schema-Drift-Selbsttest
            </h3>
            <p className="text-xs text-zinc-400" data-testid="schema-drift-badge">
              {badge}{" · "}letzter Lauf {_fmtDate(latest?.started_at)}{" · "}
              täglich 02:30 Wien
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runNow} disabled={running || loading}
            data-testid="schema-drift-run-now"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-900 disabled:text-zinc-600 border border-zinc-700">
            <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} />
            {running ? "Läuft…" : "Jetzt prüfen"}
          </button>
          {(latest?.sinks_total > 0 || (data?.history?.length || 0) > 0) && (
            <button
              onClick={() => setShowDetails(v => !v)}
              data-testid="schema-drift-toggle-details"
              className="text-xs underline text-zinc-300 hover:text-zinc-50">
              {showDetails ? "Verbergen" : "Details"}
            </button>
          )}
        </div>
      </div>

      {showDetails && (
        <div className="mt-4 space-y-3 text-xs" data-testid="schema-drift-details">
          {latest?.sinks_total > 0 && (
            <div>
              <div className="text-zinc-400 mb-1">Silent-Sinks (DB-Felder ohne Pydantic-Counterpart):</div>
              <ul className="space-y-1">
                {Object.entries(latest.sinks_by_collection || {}).map(([coll, fields]) => (
                  <li key={coll} className="font-mono">
                    <b className="text-amber-200">{coll}</b>: {fields.join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="text-zinc-400 mb-1">Letzte {(data?.history || []).length} Läufe:</div>
            <ul className="space-y-0.5 font-mono text-[10px]">
              {(data?.history || []).map((r, i) => (
                <li key={i} className="flex gap-2 text-zinc-400">
                  <span className="w-36">{_fmtDate(r.started_at)}</span>
                  <span className={
                    r.status === "ok" ? "text-emerald-300 w-10" :
                    r.status === "drift" ? "text-amber-300 w-10" : "text-rose-300 w-10"
                  }>{r.status}</span>
                  <span className="w-16">{r.sinks_total} sinks</span>
                  <span className="w-14">{r.mode}</span>
                  <span className="w-16">{r.duration_ms}ms</span>
                  <span>{r.source}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
